import {useBulkJobsState, useBulkJobsDispatch} from "../../contexts/BulkJobsContext";
import React, {useEffect, useState, useRef, useCallback} from "react";
import {Alert, Popconfirm, Progress, Button, message} from "antd";
import {CloseCircleOutlined, SyncOutlined, ClockCircleOutlined, UndoOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import VerticalSpace from "@/components/helpers/VerticalSpace";
import api from "@/components/api";
import useZeroProgressPolling from "@/components/helpers/useZeroProgressPolling";
import {bulkJobCanCancel, bulkJobTerminalAttrs} from "@/components/helpers/removalOutcome";

const STALE_THRESHOLD = 10000;  // 10 seconds without update triggers polling
const POLL_INTERVAL = 5000;     // 5 second poll interval
const INDEXING_POLL_INTERVAL = 30000; // Slow down once only search/count catch-up remains
const QUEUE_POLL_INTERVAL = 3000; // 3 second queue position poll

const BulkJobProgress = ({id, onFinish, onError, onCancel, allowUndo = true}) => {
    const {t} = useTranslation();
    const {bulkJobs, subscriptionReady} = useBulkJobsState();
    const bulkJobDispatch = useBulkJobsDispatch();
    const bulkJob = bulkJobs[id];

    const [canceling, setCanceling] = useState();
    const [canceled, setCanceled] = useState();
    const [isPolling, setIsPolling] = useState(false);
    const [queuePosition, setQueuePosition] = useState(null);
    const [undoing, setUndoing] = useState(false);
    const [undoBulkJobGuid, setUndoBulkJobGuid] = useState(null);

    const pollingRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const queuePollingRef = useRef(null);
    const lastUpdateRef = useRef(Date.now());
    const handledDoneRef = useRef(null);

    const clearProgressPolling = useCallback((updateState = true) => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        pollingIntervalRef.current = null;
        if (updateState) setIsPolling(false);
    }, []);

    // Track last update time
    useEffect(() => {
        if (bulkJob?.lastUpdate) {
            lastUpdateRef.current = bulkJob.lastUpdate;
        }
    }, [bulkJob?.lastUpdate]);

    // Poll for progress
    const pollProgress = useCallback(async () => {
        if (!id) return;

        try {
            const res = await api(`/api/bulk_jobs/${id}`);
            const job = res.data;
            const terminalAttrs = bulkJobTerminalAttrs(
                job,
                t('message-bulk-job-failed', 'Bulk job failed.')
            );

            bulkJobDispatch({
                type: 'progress',
                guid: job.guid,
                progress: job.progress,
                total_assets: job.total_assets,
                removal: job.removal,
                removed_count: job.removed_count,
                skipped_count: job.skipped_count,
                failed_count: job.failed_count,
                operation_counts: job.operation_counts,
                updated_at: job.updated_at,
                asset_group_move: job.asset_group_move,
                selected_asset_group_count: job.selected_asset_group_count,
                processed_asset_groups: job.processed_asset_groups,
                affected_asset_groups_count: job.affected_asset_groups_count,
                reindexing_asset_groups_count: job.reindexing_asset_groups_count,
                indexing_pending: job.indexing_pending,
                indexing_status: job.indexing_status,
                warnings: job.warnings,
                ...terminalAttrs
            });

            if (['processed', 'failed', 'canceled'].includes(job.aasm_state) && !job.indexing_pending) {
                clearProgressPolling();
            }
        } catch (err) {
            console.error('BulkJob poll failed:', err);
        }
    }, [id, bulkJobDispatch, clearProgressPolling, t]);

    // Start polling if stale or subscription not ready
    const startPollingIfNeeded = useCallback(() => {
        if (pollingRef.current) return;
        if ((bulkJob?.done || bulkJob?.aasm_state === 'failed' || bulkJob?.canceled) && !bulkJob?.indexing_pending) return;

        const timeSinceUpdate = Date.now() - lastUpdateRef.current;

        if (timeSinceUpdate > STALE_THRESHOLD || !subscriptionReady) {
            console.log('Starting fallback polling for BulkJob:', id, {timeSinceUpdate, subscriptionReady});
            const interval = bulkJob?.done && bulkJob?.indexing_pending ? INDEXING_POLL_INTERVAL : POLL_INTERVAL;
            setIsPolling(true);
            pollProgress();
            pollingIntervalRef.current = interval;
            pollingRef.current = setInterval(pollProgress, interval);
        }
    }, [id, bulkJob?.progress, bulkJob?.done, bulkJob?.aasm_state, bulkJob?.canceled, bulkJob?.indexing_pending, subscriptionReady, pollProgress]);

    useEffect(() => {
        if (!pollingRef.current) return;

        const interval = bulkJob?.done && bulkJob?.indexing_pending ? INDEXING_POLL_INTERVAL : POLL_INTERVAL;
        if (pollingIntervalRef.current === interval) return;

        clearInterval(pollingRef.current);
        pollingIntervalRef.current = interval;
        pollingRef.current = setInterval(pollProgress, interval);
    }, [bulkJob?.done, bulkJob?.indexing_pending, pollProgress]);

    useEffect(() => {
        if ((bulkJob?.done || bulkJob?.aasm_state === 'failed' || bulkJob?.canceled) && !bulkJob?.indexing_pending) {
            clearProgressPolling();
        }
    }, [bulkJob?.done, bulkJob?.aasm_state, bulkJob?.canceled, bulkJob?.indexing_pending, clearProgressPolling]);

    // Check for stale progress
    useEffect(() => {
        if (!id || ((bulkJob?.done || bulkJob?.aasm_state === 'failed' || bulkJob?.canceled) && !bulkJob?.indexing_pending)) return;

        const initialCheck = setTimeout(startPollingIfNeeded, STALE_THRESHOLD);
        const staleCheckInterval = setInterval(startPollingIfNeeded, STALE_THRESHOLD / 2);

        return () => {
            clearTimeout(initialCheck);
            clearInterval(staleCheckInterval);
        };
    }, [id, bulkJob?.done, bulkJob?.aasm_state, bulkJob?.canceled, bulkJob?.indexing_pending, startPollingIfNeeded]);

    useEffect(() => () => clearProgressPolling(false), [id, clearProgressPolling]);

    // Poll rapidly while at zero progress (ActionCable may not be connected yet)
    const clearZeroPolling = useZeroProgressPolling(
        pollProgress,
        !!id && !bulkJob?.progress && !bulkJob?.done && bulkJob?.aasm_state !== 'failed' && !bulkJob?.canceled
    );

    // Poll for queue position while pending
    useEffect(() => {
        if (!id || bulkJob?.progress > 0 || bulkJob?.done || bulkJob?.error || bulkJob?.canceled) {
            if (queuePollingRef.current) {
                clearInterval(queuePollingRef.current);
                queuePollingRef.current = null;
            }
            setQueuePosition(null);
            return;
        }

        const pollQueuePosition = async () => {
            try {
                const res = await api(`/api/bulk_jobs/${id}/queue_position`);
                if (res.data.aasm_state !== 'pending' || res.data.progress > 0) {
                    clearInterval(queuePollingRef.current);
                    queuePollingRef.current = null;
                    setQueuePosition(null);
                } else {
                    setQueuePosition({
                        position: res.data.queue_position,
                        depth: res.data.queue_depth
                    });
                }
            } catch (err) {
                console.error('Queue position poll failed:', err);
            }
        };

        pollQueuePosition();
        queuePollingRef.current = setInterval(pollQueuePosition, QUEUE_POLL_INTERVAL);

        return () => {
            if (queuePollingRef.current) {
                clearInterval(queuePollingRef.current);
            }
        };
    }, [id, bulkJob?.progress, bulkJob?.done, bulkJob?.error, bulkJob?.canceled]);

    // Callbacks
    useEffect(() => {
        if (!bulkJob?.done) return;
        if (handledDoneRef.current === id) return;

        handledDoneRef.current = id;
        onFinish && onFinish(bulkJob);
    }, [id, bulkJob?.progress, bulkJob?.done, bulkJob?.operation_counts, onFinish]);

    useEffect(() => {
        if (bulkJob?.error && bulkJob?.aasm_state === 'failed') onError && onError(bulkJob);
    }, [bulkJob?.error, bulkJob?.aasm_state]);

    const cancel = () => {
        setCanceling(true);
        api.post(`/api/bulk_jobs/${id}/cancel`).then(res => {
            setCanceling(false);
            setCanceled(true);
            clearProgressPolling();
            if (queuePollingRef.current) clearInterval(queuePollingRef.current);
            clearZeroPolling();
            onCancel && onCancel();
        });
    };

    const undo = () => {
        setUndoing(true);
        api.post(`/api/bulk_jobs/${id}/undo`).then(res => {
            setUndoing(false);
            setUndoBulkJobGuid(res.data.guid);
            message.info(t('message-bulk-job-undo-started', 'Undo started.'));
        }).catch(err => {
            setUndoing(false);
            const status = err?.response?.status;
            if (status === 403) {
                message.error(t('message-bulk-job-undo-forbidden', 'You do not have permission to undo this bulk job.'));
            } else {
                message.error(t('message-bulk-job-undo-failed', 'Could not start undo.'));
            }
        });
    };

    const warnings = bulkJob?.warnings?.map((warning, i) => {
        const filename = warning.asset_filename || warning.asset?.filename;
        const guid = warning.asset_guid || warning.asset?.guid;
        return (
            <div key={i} style={{marginBottom: '1em'}}>
                {filename && (
                    <div>
                        <strong>{filename} <small>({guid})</small></strong>
                    </div>
                )}
                <em>Warning: {warning.message}</em>
            </div>
        );
    });
    const wasCanceled = canceled || bulkJob?.canceled;
    const canCancel = bulkJobCanCancel(bulkJob);

    return (
        <VerticalSpace>
            {canCancel && bulkJob?.progress < 100 && (
                <VerticalSpace>
                    {/* Queue position while pending */}
                    {queuePosition && queuePosition.position !== null && (
                        <div style={{textAlign: 'center', color: '#666', marginBottom: 8}}>
                            <ClockCircleOutlined /> Position {queuePosition.position + 1} of {queuePosition.depth} in queue
                        </div>
                    )}

                    <Progress
                        percent={bulkJob?.progress || 0}
                        status={bulkJob?.error ? 'error' : 'active'}
                    />

                    {isPolling && (
                        <small style={{color: '#888'}}>
                            <SyncOutlined spin /> Using fallback updates
                        </small>
                    )}

                    <Popconfirm
                        title={t('cancel-job', 'Cancel Bulk Job?')}
                        onConfirm={cancel}
                        zIndex={9999}
                    >
                        <Button danger ghost icon={<CloseCircleOutlined />} loading={canceling} size={'small'}>
                            Cancel
                        </Button>
                    </Popconfirm>
                </VerticalSpace>
            )}

            {bulkJob?.error && <Alert message={bulkJob.error} type="error" showIcon />}
            {bulkJob?.asset_group_move && bulkJob?.indexing_pending && (
                <Alert
                    message={bulkJob.indexing_status || t('asset-group-move-indexing-pending', 'Move complete. Search and counts are catching up.')}
                    type="info"
                    showIcon
                />
            )}
            {!!warnings?.length && <Alert message={warnings} type="warning" showIcon />}
            {wasCanceled && (
                <Alert message={t('message-bulk-job-canceled', `Bulk job canceled.`)} type="warning" showIcon />
            )}

            {allowUndo && bulkJob?.done && !bulkJob?.error && !wasCanceled && !undoBulkJobGuid && (
                <Popconfirm
                    title={t('confirm-undo-bulk-job', 'Undo this bulk job? Fields you have changed since will be preserved.')}
                    onConfirm={undo}
                    zIndex={9999}
                >
                    <Button icon={<UndoOutlined />} loading={undoing} size={'small'}>
                        {t('undo-bulk-job', 'Undo')}
                    </Button>
                </Popconfirm>
            )}

            {undoBulkJobGuid && (
                <div>
                    <small style={{color: '#888'}}>{t('undo-in-progress', 'Undo in progress')}</small>
                    <BulkJobProgress id={undoBulkJobGuid} allowUndo={false}/>
                </div>
            )}
        </VerticalSpace>
    );
};

export default BulkJobProgress;
